import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnRecordingRetentionSchedule } from '@lib/api/entities';
import { t } from '@lib/translation/helpers/translation.helper';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnRecordingRetentionScheduleAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getRetentionSchedule(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnRecordingRetentionSchedule>> {
    return this._httpClient.get('/api/recordings/retention-schedules/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((result) => new SnRecordingRetentionSchedule(result)),
      })),
    );
  }

  public feedRetentionSchedule(
    params: ISnFeedQueryParameters,
  ): Observable<SnRecordingRetentionSchedule[]> {
    return this._httpClient
      .get('/api/recordings/retention-schedules/feed/', { params })
      .pipe(map((response: any) => response.map((data) => new SnRecordingRetentionSchedule(data))));
  }

  public createRetentionSchedule(data: Object): Observable<SnRecordingRetentionSchedule> {
    return this._httpClient.post('/api/recordings/retention-schedules/', data).pipe(
      map((response: Object) => new SnRecordingRetentionSchedule(response)),
      tap({
        next: () => this._toastrService.success(t('settings.retentionSchedule.messages.created')),
        error: () =>
          this._toastrService.error(t('settings.retentionSchedule.messages.createError')),
      }),
    );
  }

  public updateRetentionSchedule(
    id: string,
    data: Object,
  ): Observable<SnRecordingRetentionSchedule> {
    return this._httpClient.patch(`/api/recordings/retention-schedules/${id}/`, data).pipe(
      map((response: Object) => new SnRecordingRetentionSchedule(response)),
      tap({
        next: () => this._toastrService.success(t('settings.retentionSchedule.messages.updated')),
        error: () =>
          this._toastrService.error(t('settings.retentionSchedule.messages.updateError')),
      }),
    );
  }

  public deleteRetentionSchedule(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('settings.retentionSchedule.messages.deleteConfirm'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/recordings/retention-schedules/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('settings.retentionSchedule.messages.deleted')),
          error: () =>
            this._toastrService.error(t('settings.retentionSchedule.messages.deleteError')),
        }),
      );
  }
}
